import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { deleteMove } from '@/api/moves'
import {
  movesKeys,
  seriesKeys,
  moveConnectionsKeys,
  taggingsKeys,
  dashboardKeys,
} from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to delete a move
 *
 * Handles:
 * - API call to soft delete move
 * - Cache invalidation for all move queries
 * - Navigation to moves list on success
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const deleteMove = useDeleteMove()
 *
 * const handleDelete = async () => {
 *   if (confirm('Are you sure?')) {
 *     await deleteMove.mutateAsync('move-123')
 *   }
 * }
 * ```
 */
export function useDeleteMove() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (moveId: string) => deleteMove(moveId),
    onSuccess: () => {
      // Invalidate all affected queries (backend cascade deletes series_moves, move_connections, taggings)
      queryClient.invalidateQueries({ queryKey: movesKeys.all })
      queryClient.invalidateQueries({ queryKey: seriesKeys.all })
      queryClient.invalidateQueries({ queryKey: moveConnectionsKeys.all })
      queryClient.invalidateQueries({ queryKey: taggingsKeys.all })
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all })

      // Show success toast
      toast({
        title: 'Move deleted',
        description: 'The move has been deleted successfully.',
      })

      // TODO: Add telemetry when gtag is properly configured

      // Navigate to moves list
      navigate('/moves')
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to delete move',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })

      // TODO: Add telemetry when gtag is properly configured
    },
  })
}
